import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch { setUser(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    window.location.href = "/login";
  };

  // Auto-logout after inactivity (default: 30 minutes). Any mouse/keyboard/touch activity resets the timer.
  useEffect(() => {
    if (!user) return;
    const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        try { window.alert("Signed out due to 30 minutes of inactivity. Please sign in again."); } catch {}
        logout();
      }, INACTIVITY_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, checkAuth, logout, isAdmin: user?.role === "admin", isHR: user?.role === "hr", isSales: user?.role === "sales", isEmployee: user?.role === "employee", role: user?.role }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export const VENDOR_CLASS = { Cisco:"vendor-cisco", Fortinet:"vendor-fortinet", "Palo Alto":"vendor-palo", F5:"vendor-f5", Checkpoint:"vendor-checkpoint" };
export const STAGES = [
  { key: "deal", label: "Deal" },
  { key: "discovery", label: "Discovery" },
  { key: "scope-in-progress", label: "Scope in Progress" },
  { key: "sow-proposal-ready", label: "SOW Ready" },
  { key: "awaiting-signature", label: "Awaiting Signature" },
  { key: "closed-won", label: "Closed Won" },
  { key: "config-in-progress", label: "Config In Progress" },
  { key: "deployment", label: "Deployment" },
  { key: "delivered", label: "Delivered" },
  { key: "rework", label: "Rework" },
  { key: "closed-lost", label: "Closed Lost" },
];
export const STAGE_COLORS = {
  deal: "text-blue-700 bg-blue-50 border-blue-200",
  discovery: "text-indigo-700 bg-indigo-50 border-indigo-200",
  "scope-in-progress": "text-purple-700 bg-purple-50 border-purple-200",
  "sow-proposal-ready": "text-violet-700 bg-violet-50 border-violet-200",
  "awaiting-signature": "text-amber-700 bg-amber-50 border-amber-200",
  "closed-won": "text-emerald-700 bg-emerald-50 border-emerald-200",
  "config-in-progress": "text-cyan-700 bg-cyan-50 border-cyan-200",
  deployment: "text-orange-700 bg-orange-50 border-orange-200",
  delivered: "text-slate-700 bg-slate-100 border-slate-300",
  rework: "text-red-700 bg-red-50 border-red-200",
  "closed-lost": "text-slate-600 bg-slate-100 border-slate-300",
  // legacy stages still accepted from older seed data
  planning: "text-amber-700 bg-amber-50 border-amber-200",
  sow: "text-violet-700 bg-violet-50 border-violet-200",
  proposal: "text-violet-700 bg-violet-50 border-violet-200",
  won: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

// Australian states/territories — used across leads/clients/site-access.
export const AU_STATES = [
  { code: "NSW", label: "New South Wales" },
  { code: "VIC", label: "Victoria" },
  { code: "QLD", label: "Queensland" },
  { code: "WA",  label: "Western Australia" },
  { code: "SA",  label: "South Australia" },
  { code: "TAS", label: "Tasmania" },
  { code: "ACT", label: "Australian Capital Territory" },
  { code: "NT",  label: "Northern Territory" },
];
export const fmtCurrency = (n) => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", maximumFractionDigits:0 }).format(n || 0);

export function formatApiError(detail, fallback = "Something went wrong.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
